/**
 * Formulas menu: list of saved formulas to choose from
 */
import {jsx} from 'panel';

import {InputComponent} from 'common/components/input';
import {ItemClickEvent, Section, SectionItem} from 'common/components/items-menu/types';
import {DummyStorageManager} from 'common/contexts/storage-manager/dummy';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {ItemsMenuEl} from 'common/components/items-menu';
import {SkeletonListEl} from 'common/components/skeleton-list';
import {SkeletonListRowSize} from 'common/components/skeleton-list/types';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {getResolvedFormulaEntry} from 'common/util/formulas';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {FeatureGate, Permission} from 'common/report/constants';

import {ChildMenu} from '../child-menu';
import {MenuSearchBar} from '../mixins/search';
import css from './index.inline.styl';
import {
  createContextPanelEventHandlers,
  generateFormulaContextPanelData,
  renderMetricContextPanel,
} from './context-panel';
import {FormulasMenuItem, FormulasMenuItemType} from './types';

interface FormulasMenuState {
  searchFilter: string;
}

export class FormulasMenu extends ChildMenu<FormulasMenuState> {
  static contextPanelRenderer = renderMetricContextPanel;
  public searchInputEl: InputComponent = null;

  get config() {
    return {
      contexts: [`sessionStorageManager` as const, `currentProject` as const, `metrics` as const],
      defaultContexts: {
        sessionStorageManager: new DummyStorageManager(),
        metrics: new MetricsResource(),
      },
      defaultState: {
        searchFilter: ``,
      },
      helpers: {
        ...super.config.helpers,
      },
      useShadowDom: true,
      css,
      template: () => {
        const isLoading = this.getContext(`metrics`).isLoadingAll();

        const InnerSearch = MenuSearchBar({
          value: this.state.searchFilter,
          handleSearchInput: (value) => this.update({searchFilter: value}),
          onInsert: (el) => this.update({keydownProxyEl: el}),
          placeholder: `Search Formulas`,
          newStyling: true,
        });

        return (
          <div
            sel='.mp-formulas-menu'
            class={{'mp-child-menu': true}}
            hook={{insert: (vnode) => this.helpers.setChildMenuRef(vnode)}}
          >
            {this.attr(`open`) && (
              <>
                {InnerSearch}
                {isLoading ? (
                  <SkeletonListEl
                    attrs={{'num-rows': 6, 'row-size': SkeletonListRowSize.Large}}
                    sel='.mp-formulas-menu-skeleton-list'
                  />
                ) : (
                  <ItemsMenuEl
                    sel='.mp-formulas-menu-items-menu'
                    attrs={{
                      elref: `mainSelectionMenu`,
                      'empty-message': `No items found`,
                      multiple: false,
                      'search-filter': this.state.searchFilter,
                      'start-focus-on-selected': true,
                      'disable-search-sort': true,
                      'disable-search-filter-matching-parts': true,
                      'disable-first-item-padding': true,
                      'new-styling': true,
                    }}
                    on={{
                      click: (ev) => ev.stopPropagation(),
                      clickItem: (ev) => this.handleClickItem(ev),
                      ...createContextPanelEventHandlers({
                        availableContexts: {
                          metrics: this.getContext(`metrics`),
                        },
                      }),
                    }}
                    props={{
                      sections: this.getSections(),
                      keydownProxyEl: this.state.keydownProxyEl,
                      contextPanelTransform: (item: SectionItem<FormulasMenuItem>) => {
                        return item.value.type === FormulasMenuItemType.SavedFormula
                          ? this.getTooltipData(item.value.id)
                          : null;
                      },
                      contextPanelPositionParentElement: this.helpers.getChildMenuRef(),
                      sectionsFromInputFn: this.sectionsFromInput,
                    }}
                  />
                )}
                <ButtonEl
                  sel='.create-custom-button'
                  attrs={{
                    'show-preview-tooltip': false,
                    theme: ButtonTheme.MenuFooter,
                    'hide-input-focus-styling': true,
                    label: `Create Custom`,
                  }}
                  on={{
                    click: () => this.handleClickCreate(),
                  }}
                >
                  <div sel='.create-custom-label' attrs={{elref: `label`}}>
                    Create Custom
                  </div>
                </ButtonEl>
              </>
            )}
          </div>
        );
      },
    };
  }

  canShare() {
    return this.getContext(`currentProject`).get()?.value?.getFeatureGate(FeatureGate.Sharing);
  }

  canWriteMetrics() {
    return this.getContext(`currentProject`).get()?.value?.hasPermission(Permission.WriteMetrics);
  }

  handleClickCreate() {
    this.dispatchEvent(new CustomEvent(`create`));
  }

  handleClickItem({detail}: ItemClickEvent<FormulasMenuItem>) {
    const {item} = detail;

    this.dispatchEvent(new CustomEvent(`select`, {detail: {item: item.value}}));
  }

  getPersistenceKey() {
    return `formulas-menu`;
  }

  getSections(): Array<Section<FormulasMenuItem>> {
    /** filters metrics according to the user's permissions and sharing */
    const savedFormulas = this.getContext(`metrics`)
      .getAllValues()
      .filter((metric) => metric.type === `formula` && metric.can_view);

    if (savedFormulas.length) {
      return [
        {
          items: savedFormulas.map((metric) => ({
            label: metric.name,
            value: {
              type: FormulasMenuItemType.SavedFormula,
              id: metric.id,
            },
          })),
        },
      ];
    } else {
      return [];
    }
  }

  /** appends item for an inline formula using the searchFilter as the definition */
  sectionsFromInput = (searchFilter: string): Array<Section<FormulasMenuItem>> => {
    const label = `"${searchFilter}"`;
    return [
      {
        items: [
          {
            label,
            value: {
              type: FormulasMenuItemType.InlineFormula,
              definition: searchFilter,
            },
            itemFromInput: true,
          },
        ],
      },
    ];
  };

  selectedLabel() {
    const selected = this.attr(`selected`);

    if (selected?.id) {
      return (
        getResolvedFormulaEntry({
          metricsResource: this.getContext(`metrics`),
          formulaEntry: {event: selected},
        })?.event.name || `Loading...`
      );
    }

    return `Select Formula`;
  }

  getSelectedHoverTooltipData() {
    return this.getTooltipData(this.attr(`selected`)?.id);
  }

  getTooltipData(id: number): ContextPanelData {
    return generateFormulaContextPanelData({
      id,
      metricsContext: this.getContext(`metrics`),
      canWriteMetrics: this.canWriteMetrics(),
      canShare: this.canShare(),
    });
  }
}

export const FormulasMenuEl = defineAndMakeJsxFactory(`mp-formulas-menu`, FormulasMenu);
export const FormulasMenuReact = panel2react(`mp-formulas-menu`, FormulasMenu);
